import { CheckCircle2, Search, SlidersHorizontal } from "lucide-react";
import { useMemo, useState } from "react";
import { EmptyState } from "../components/EmptyState";
import { FactCard } from "../components/FactCard";
import { FilterChips } from "../components/FilterChips";
import { LastUpdated } from "../components/LastUpdated";
import { PageHeader } from "../components/PageHeader";
import { factCategories } from "../data/categories";
import { verifiedFacts } from "../data/verifiedFacts";
import { normalizeSearch } from "../lib/search";

const statusOptions = [
  { id: "all", label: "Все статусы" },
  { id: "confirmed", label: "Подтверждено" },
  { id: "needs-review", label: "Требует проверки" },
  { id: "archived", label: "Архив" },
];

const usageOptions = [
  { id: "all", label: "Любая публичность" },
  { id: "allowed", label: "Разрешено публично" },
  { id: "restricted", label: "После проверки" },
  { id: "internal-only", label: "Только внутри" },
];

export function FactsPage() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [status, setStatus] = useState("all");
  const [usage, setUsage] = useState("all");
  const [onlyConfirmed, setOnlyConfirmed] = useState(false);
  const [sort, setSort] = useState<"newest" | "oldest">("newest");

  const facts = useMemo(() => {
    const needle = normalizeSearch(query);
    return verifiedFacts
      .filter((fact) => !needle || normalizeSearch([
        fact.title,
        fact.value,
        fact.category,
        fact.sourceTitle,
        ...fact.tags,
      ].join(" ")).includes(needle))
      .filter((fact) => category === "all" || fact.category === category)
      .filter((fact) => (onlyConfirmed ? fact.status === "confirmed" : status === "all" || fact.status === status))
      .filter((fact) => usage === "all" || fact.publicUsage === usage)
      .sort((a, b) => sort === "newest"
        ? b.lastVerifiedAt.localeCompare(a.lastVerifiedAt)
        : a.lastVerifiedAt.localeCompare(b.lastVerifiedAt));
  }, [query, category, status, usage, onlyConfirmed, sort]);

  return (
    <div className="page">
      <PageHeader
        eyebrow="Раздел 02"
        title="Банк подтверждённых фактов"
        description="Цифры и утверждения с понятным статусом, источником и датой проверки."
      />
      <LastUpdated date="2026-07-15" section />

      <div className="facts-summary">
        <div><strong>{verifiedFacts.length}</strong><span>фактов в базе</span></div>
        <div><strong>{verifiedFacts.filter((fact) => fact.status === "confirmed").length}</strong><span>подтверждено</span></div>
        <div><strong>{verifiedFacts.filter((fact) => fact.status === "needs-review").length}</strong><span>нужна проверка</span></div>
      </div>

      <section className="filters-panel" aria-label="Фильтры фактов">
        <div className="filters-panel__search">
          <Search size={19} aria-hidden="true" />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Найти факт или цифру" aria-label="Найти факт или цифру" />
        </div>
        <FilterChips
          label="Категория"
          value={category}
          onChange={setCategory}
          options={[{ id: "all", label: "Все категории" }, ...factCategories.map((item) => ({ id: item, label: item }))]}
        />
        <div className="filter-row">
          <label>
            <span>Статус</span>
            <select value={status} onChange={(event) => setStatus(event.target.value)} disabled={onlyConfirmed}>
              {statusOptions.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select>
          </label>
          <label>
            <span>Публичность</span>
            <select value={usage} onChange={(event) => setUsage(event.target.value)}>
              {usageOptions.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select>
          </label>
          <label>
            <span>Сортировка</span>
            <select value={sort} onChange={(event) => setSort(event.target.value as "newest" | "oldest")}>
              <option value="newest">Сначала новые</option>
              <option value="oldest">Сначала старые</option>
            </select>
          </label>
        </div>
        <label className="toggle-row">
          <button type="button" role="switch" aria-checked={onlyConfirmed} className={`toggle ${onlyConfirmed ? "is-active" : ""}`} onClick={() => setOnlyConfirmed((value) => !value)}>
            <span />
          </button>
          <CheckCircle2 size={18} aria-hidden="true" />
          <span>Показывать только подтверждённые</span>
        </label>
      </section>

      <div className="results-meta"><SlidersHorizontal size={16} /> Найдено: {facts.length}</div>
      {facts.length > 0 ? (
        <div className="facts-grid">{facts.map((fact) => <FactCard key={fact.id} fact={fact} />)}</div>
      ) : (
        <EmptyState description="В выбранной категории пока нет подходящих подтверждённых фактов." />
      )}
    </div>
  );
}
